import type { UiStatusSidebarItem } from "@shared/components/UiStatusSidebar/index.vue";
import { t } from "@shared/utils/i18n";

/** 状态筛选：正常同步 / 暂停同步（筛选区下拉） */
/** 同步状态筛选：1 正常同步 / 0 暂停同步（请求体 syncStatuses） */
export type DfSellableSyncStatus = 0 | 1;

export const DF_SELLABLE_STATUS_OPTIONS = [
    { label: t("正常同步"), value: 1 },
    { label: t("暂停同步"), value: 0 },
];

/** @deprecated 使用 syncStatuses: number[] */
export type DfSellableStatusFilter = "" | "normal" | "paused";

/**
 * 快捷筛选 bucket（与 storeList.zoneStockSummary / list.zoneStockBuckets 对齐）
 * zone_1 | zone_2 | zone_3 | zone_3_plus | no_stock
 */
export type DfZoneStockBucket = "zone_1" | "zone_2" | "zone_3" | "zone_3_plus" | "no_stock";

/** @deprecated 使用 DfZoneStockBucket */
export type DfZoneStockQuickFilter = "" | DfZoneStockBucket;

export interface DfQuickFilterOption {
    value: DfZoneStockBucket;
    label: string;
    count: number;
}

export const DF_ZONE_STOCK_BUCKETS: DfZoneStockBucket[] = ["zone_1", "zone_2", "zone_3", "zone_3_plus", "no_stock"];

export const DF_QUICK_FILTER_DEFS: Array<Omit<DfQuickFilterOption, "count">> = [
    { value: "zone_1", label: t("一区有库存") },
    { value: "zone_2", label: t("二区有库存") },
    { value: "zone_3", label: t("三区有库存") },
    { value: "zone_3_plus", label: t("三区以上有库存") },
    { value: "no_stock", label: t("无库存") },
];

export const EMPTY_DF_ZONE_STOCK_COUNTS: Record<DfZoneStockBucket, number> = {
    zone_1: 0,
    zone_2: 0,
    zone_3: 0,
    zone_3_plus: 0,
    no_stock: 0,
};

export type DfZoneStockSummaryCounts = Partial<Record<DfZoneStockBucket, number>>;

function isZoneStockBucket(value: string): value is DfZoneStockBucket {
    return (DF_ZONE_STOCK_BUCKETS as string[]).includes(value);
}

/** storeList.items[].zoneStockSummary → 侧栏二级数量 */
export function mapDfZoneStockSummaryToCounts(data: unknown): Record<DfZoneStockBucket, number> {
    const counts: Record<DfZoneStockBucket, number> = { ...EMPTY_DF_ZONE_STOCK_COUNTS };
    if (!data || typeof data !== "object") return counts;

    const record = data as Record<string, unknown>;
    const items = Array.isArray(record.items) ? record.items : null;
    if (!items) return counts;

    for (const raw of items) {
        if (!raw || typeof raw !== "object") continue;
        const item = raw as Record<string, unknown>;
        const value = String(item.value ?? "").trim();
        if (!isZoneStockBucket(value)) continue;
        counts[value] = Number(item.cnt ?? item.count ?? 0) || 0;
    }
    return counts;
}

export function buildDfQuickFilterOptions(counts?: DfZoneStockSummaryCounts): DfQuickFilterOption[] {
    return DF_QUICK_FILTER_DEFS.map(item => ({
        ...item,
        count: counts?.[item.value] ?? 0,
    }));
}

/** 侧栏店铺源：含 storeList 内嵌 zoneStockSummary */
export interface DfSellableStoreSidebarSource {
    storeId: string;
    label: string;
    zoneStockSummary?: {
        total?: number;
        items?: Array<{ value?: string; label?: string; cnt?: number; count?: number }>;
    };
}

/**
 * 构建 DF 可售库存店铺侧栏：
 * 一级 = 店铺；二级 = 该店铺 zoneStockSummary.items（数量不再另调接口）
 */
export function buildDfSellableStoreSidebarItems(stores: DfSellableStoreSidebarSource[]): UiStatusSidebarItem[] {
    return stores.map(store => {
        const counts = mapDfZoneStockSummaryToCounts(store.zoneStockSummary);
        const labelByValue = new Map<string, string>();
        for (const raw of store.zoneStockSummary?.items ?? []) {
            const value = String(raw?.value ?? "").trim();
            const label = String(raw?.label ?? "").trim();
            if (value && label) labelByValue.set(value, label);
        }
        return {
            key: store.storeId,
            label: store.label,
            children: DF_QUICK_FILTER_DEFS.map(def => ({
                key: def.value,
                label: labelByValue.get(def.value) || def.label,
                count: counts[def.value] ?? 0,
            })),
        };
    });
}
